import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { getAllScreens, getAllCheckpoints, deleteScreen, deleteCheckpoint } from '../../services/funnelService';
import ScreenEditor from './ScreenEditor';
import BackupManager from './BackupManager';
import AnalyticsScreen from './AnalyticsScreen';
import OptimalPaceAnalysisScreen from './OptimalPaceAnalysisScreen';
import PressureScoreAnalysisScreen from './PressureScoreAnalysisScreen';
import UserLookupScreen from './UserLookupScreen';
import GroupsScreen from './GroupsScreen';
import SyncScreen from './SyncScreen';
import AttributionConversionReportScreen from './AttributionConversionReportScreen';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.doryBeige};
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  max-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.doryBlue};
  margin: 0 0 ${theme.spacing.xs};
  letter-spacing: ${theme.typography.letterSpacing.tight};
  line-height: ${theme.typography.lineHeights.tight};
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.regular};
  color: ${theme.colors.doryGreen};
  margin: 0;
  letter-spacing: ${theme.typography.letterSpacing.wide};
  text-transform: uppercase;
`;

const LogoutButton = styled.button`
  flex-shrink: 0;
  background: transparent;
  color: ${theme.colors.doryDarkerBlue};
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  letter-spacing: ${theme.typography.letterSpacing.wide};

  &:hover {
    background: ${theme.colors.doryBlue};
    border-color: ${theme.colors.doryBlue};
    color: white;
  }
`;

const Nav = styled.nav`
  margin-bottom: ${theme.spacing.lg};
  background: white;
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs};
`;

const NavList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
`;

const NavButton = styled.button`
  background: ${props => props.active ? theme.colors.doryBlue : 'transparent'};
  color: ${props => props.active ? 'white' : theme.colors.doryDarkerBlue};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${props => props.active ? theme.typography.weights.semiBold : theme.typography.weights.medium};
  cursor: pointer;
  transition: background ${theme.animation.durations.fast}, color ${theme.animation.durations.fast};
  white-space: nowrap;
  line-height: 1.3;

  &:hover {
    background: ${props => props.active ? theme.colors.doryBlue : theme.colors.doryBeige};
    color: ${props => props.active ? 'white' : theme.colors.doryBlue};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ItemCard = styled.div`
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.sm};
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const ItemDetails = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin: 0;
`;

const ItemActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button`
  background: ${props => 
    props.variant === 'edit' ? theme.colors.doryBlue : 
    props.variant === 'delete' ? theme.colors.error : 
    theme.colors.doryBlue};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    opacity: 0.8;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: ${theme.shadows.lg};
`;

const ModalTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const ModalMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: ${theme.typography.lineHeights.normal};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  background: ${props => props.variant === 'danger' ? theme.colors.error : 'white'};
  color: ${props => props.variant === 'danger' ? 'white' : theme.colors.text};
  border: 2px solid ${props => props.variant === 'danger' ? theme.colors.error : theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${theme.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: white;
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  outline: none;
  transition: border-color ${theme.animation.durations.fast};

  &:focus {
    border-color: ${theme.colors.primary};
  }

  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.doryLighterBlue};
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  line-height: 1;
  padding: 0;

  &:hover {
    background: ${theme.colors.primary};
  }
`;

const AdminPanel = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('screens');
  const [screens, setScreens] = useState({});
  const [checkpoints, setCheckpoints] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [screensData, checkpointsData] = await Promise.all([
        getAllScreens(),
        getAllCheckpoints()
      ]);
      setScreens(screensData);
      setCheckpoints(checkpointsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    setEditingType(type);
  };

  const handleAdd = (type) => {
    setEditingItem(null);
    setEditingType(type);
  };

  const handleSave = async () => {
    // Refresh data after save
    await loadData();
    setEditingItem(null);
    setEditingType(null);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditingType(null);
  };

  const getNextScreenDisplay = (nextScreen) => {
    if (!nextScreen) {
      return 'None';
    }
    
    if (typeof nextScreen === 'string') {
      if (/^https?:\/\//i.test(nextScreen)) {
        return `URL: ${nextScreen}`;
      }
      return nextScreen;
    }
    
    if (typeof nextScreen === 'object') {
      // Handle conditional navigation object
      if (nextScreen.default) {
        const defaultDisplay = /^https?:\/\//i.test(nextScreen.default)
          ? `URL: ${nextScreen.default}`
          : nextScreen.default;
        return `Conditional (default: ${defaultDisplay})`;
      }
      return 'Conditional';
    }
    
    return 'Unknown';
  };

  const handleDelete = (item, type) => {
    setDeleteConfirm({ item, type });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      if (deleteConfirm.type === 'screen') {
        await deleteScreen(deleteConfirm.item.id);
      } else {
        await deleteCheckpoint(deleteConfirm.item.id);
      }
      
      // Refresh data after deletion
      await loadData();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      // Could add error state here if needed
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const getFilteredSortedItems = (items) => {
    const query = searchQuery.trim().toLowerCase();
    return Object.values(items)
      .filter((item) => !query || (item.key || '').toLowerCase().includes(query))
      .sort((a, b) => (a.key || '').localeCompare(b.key || ''));
  };

  const renderKeySearch = () => (
    <SearchBar>
      <SearchInput
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Filter by key..."
        aria-label="Filter by key"
      />
      {searchQuery && (
        <ClearSearchButton
          type="button"
          onClick={() => setSearchQuery('')}
          aria-label="Clear search"
        >
          ×
        </ClearSearchButton>
      )}
    </SearchBar>
  );

  if (editingType) {
    return (
      <ScreenEditor
        screen={editingItem}
        type={editingType}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading admin data...</LoadingMessage>
      </Container>
    );
  }

  const renderScreens = () => {
    const totalCount = Object.keys(screens).length;
    const screenList = getFilteredSortedItems(screens);
    
    return (
      <ContentCard>
        <SectionHeader>
          <SectionTitle>Screens ({screenList.length})</SectionTitle>
          <AddButton onClick={() => handleAdd('screen')}>
            + Add New Screen
          </AddButton>
        </SectionHeader>

        {renderKeySearch()}
        
        <ItemsList>
          {screenList.map((screen) => (
            <ItemCard key={screen.key}>
              <ItemInfo>
                <ItemName>{screen.key}</ItemName>
                <ItemDetails>
                  Type: {screen.type} | Next: {getNextScreenDisplay(screen.nextScreen)}
                </ItemDetails>
              </ItemInfo>
              <ItemActions>
                <ActionButton 
                  variant="edit" 
                  onClick={() => handleEdit(screen, 'screen')}
                >
                  Edit
                </ActionButton>
                <ActionButton 
                  variant="delete" 
                  onClick={() => handleDelete(screen, 'screen')}
                >
                  Delete
                </ActionButton>
              </ItemActions>
            </ItemCard>
          ))}
          {screenList.length === 0 && (
            <LoadingMessage>
              {totalCount === 0
                ? 'No screens found. Add your first screen!'
                : 'No screens match your search.'}
            </LoadingMessage>
          )}
        </ItemsList>
      </ContentCard>
    );
  };

  const renderCheckpoints = () => {
    const totalCount = Object.keys(checkpoints).length;
    const checkpointList = getFilteredSortedItems(checkpoints);
    
    return (
      <ContentCard>
        <SectionHeader>
          <SectionTitle>Checkpoints ({checkpointList.length})</SectionTitle>
          <AddButton onClick={() => handleAdd('checkpoint')}>
            + Add New Checkpoint
          </AddButton>
        </SectionHeader>

        {renderKeySearch()}
        
        <ItemsList>
          {checkpointList.map((checkpoint) => (
            <ItemCard key={checkpoint.key}>
              <ItemInfo>
                <ItemName>{checkpoint.key}</ItemName>
                <ItemDetails>
                  Type: {checkpoint.type} | Next: {getNextScreenDisplay(checkpoint.nextScreen)}
                </ItemDetails>
              </ItemInfo>
              <ItemActions>
                <ActionButton 
                  variant="edit" 
                  onClick={() => handleEdit(checkpoint, 'checkpoint')}
                >
                  Edit
                </ActionButton>
                <ActionButton 
                  variant="delete" 
                  onClick={() => handleDelete(checkpoint, 'checkpoint')}
                >
                  Delete
                </ActionButton>
              </ItemActions>
            </ItemCard>
          ))}
          {checkpointList.length === 0 && (
            <LoadingMessage>
              {totalCount === 0
                ? 'No checkpoints found. Add your first checkpoint!'
                : 'No checkpoints match your search.'}
            </LoadingMessage>
          )}
        </ItemsList>
      </ContentCard>
    );
  };

  const navItems = [
    { id: 'screens', label: 'Screens' },
    { id: 'checkpoints', label: 'Checkpoints' },
    { id: 'backup', label: 'Backup' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'optimal-pace', label: 'Optimal Pace' },
    { id: 'pressure-score', label: 'Pressure Score' },
    { id: 'userlookup', label: 'User Lookup' },
    { id: 'groups', label: 'Groups' },
    { id: 'sync', label: 'Sync' },
    { id: 'attribution', label: 'Attribution' },
  ];

  return (
    <Container>
      <Header>
        <HeaderText>
          <Title>Optimal Pace Protocol</Title>
          <Subtitle>Admin Tools and Reporting</Subtitle>
        </HeaderText>
        <LogoutButton onClick={onLogout}>
          Log Out
        </LogoutButton>
      </Header>

      <Nav aria-label="Admin tools">
        <NavList>
          {navItems.map((item) => (
            <NavButton
              key={item.id}
              type="button"
              active={activeTab === item.id}
              onClick={() => setActiveTab(item.id)}
            >
              {item.label}
            </NavButton>
          ))}
        </NavList>
      </Nav>

      {activeTab === 'screens' && renderScreens()}
      {activeTab === 'checkpoints' && renderCheckpoints()}
      {activeTab === 'backup' && <BackupManager />}
      {activeTab === 'analytics' && <AnalyticsScreen />}
      {activeTab === 'optimal-pace' && <OptimalPaceAnalysisScreen />}
      {activeTab === 'pressure-score' && <PressureScoreAnalysisScreen />}
      {activeTab === 'userlookup' && <UserLookupScreen />}
      {activeTab === 'groups' && <GroupsScreen />}
      {activeTab === 'sync' && <SyncScreen />}
      {activeTab === 'attribution' && <AttributionConversionReportScreen />}

      {deleteConfirm && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirm Deletion</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete the {deleteConfirm.type} "{deleteConfirm.item.key}"? 
              This action cannot be undone.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={cancelDelete} disabled={deleting}>
                Cancel
              </ModalButton>
              <ModalButton variant="danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AdminPanel; 